import { useState } from 'react';
import { resolveTeamLogoUrl, teamInitials, leagueGlyph, leagueLogoUrl } from '../../lib/teamLogos';

/**
 * Centralized team logo. Renders a licensed self-hosted logo when the pack is
 * enabled and available; otherwise the premium black-and-gold initials badge.
 * A broken image can never appear (onError swaps to the initials badge).
 */
export const TeamLogo = ({ name, sportKey, teamId, abbr, size = 32, className = '' }) => {
    const [failed, setFailed] = useState(false);
    const url = failed ? null : resolveTeamLogoUrl({ sportKey, teamId, abbr, name });
    const box = {
        width: size, height: size,
        background: 'radial-gradient(circle at 30% 25%, #232323, #0c0c0c)',
        border: '1.5px solid rgba(255,215,0,0.35)',
        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.6)',
    };
    return (
        <span
            className={`inline-flex items-center justify-center rounded-full shrink-0 overflow-hidden ${className}`}
            style={box}
            title={name}
            data-testid={`team-logo-${(name || '').toLowerCase().replace(/\s+/g, '-')}`}
        >
            {url ? (
                <img
                    src={url}
                    alt={name}
                    onError={() => setFailed(true)}
                    style={{ width: '82%', height: '82%', objectFit: 'contain' }}
                />
            ) : (
                <span
                    className="font-black leading-none"
                    style={{
                        fontSize: Math.max(9, size * 0.36),
                        color: '#FFD700',
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 1px rgba(0,0,0,0.6)',
                    }}
                >
                    {teamInitials(name)}
                </span>
            )}
        </span>
    );
};

/** League chip icon — official league logo when available, else a generic sport glyph, in a gold-ringed disc. */
export const LeagueIcon = ({ sportKey, label, size = 22 }) => {
    const [failed, setFailed] = useState(false);
    const url = failed ? null : leagueLogoUrl(sportKey);
    return (
        <span
            className="inline-flex items-center justify-center rounded-full shrink-0 overflow-hidden"
            style={{
                width: size, height: size,
                background: 'radial-gradient(circle at 30% 25%, #232323, #0c0c0c)',
                border: '1px solid rgba(255,215,0,0.3)',
                fontSize: size * 0.62,
            }}
            aria-label={label}
        >
            {url ? (
                <img
                    src={url}
                    alt={label || ''}
                    onError={() => setFailed(true)}
                    style={{ width: '78%', height: '78%', objectFit: 'contain' }}
                />
            ) : (
                <span className="leading-none">{leagueGlyph(sportKey)}</span>
            )}
        </span>
    );
};

export default TeamLogo;
